/**
 * Lop goi API.
 *
 * Ba viec tep nay chiu trach nhiem, va khong noi nao khac duoc lam thay:
 *   1. Gan ma truy cap vao moi yeu cau can xac thuc.
 *   2. Sinh khoa bat bien cho MOI thao tac ghi tien (quy uoc ID-01 cua may chu).
 *   3. Doc phong bi loi cua may chu thanh mot kieu loi dung nhat, ke ca truong hop
 *      may chu tra ve NHIEU ly do tu choi cung luc.
 */

import type { MoneyDto } from './money';

const TOKEN_KEY = 'gikidi.access_token';

export interface ApiErrorDetail {
  code: string;
  message: string;
  trace_id: string;
  details: Record<string, unknown>;
}

/** Mot ly do tu choi. May chu tra ve nhieu ly do khi kiem tra dieu kien rut tien. */
export interface RejectionReason {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly traceId: string;
  readonly details: Record<string, unknown>;
  /**
   * May chu tra ve TOAN BO ly do tu choi chu khong dung o ly do dau tien.
   * Giao dien phai hien thi het — bat nguoi choi sua tung loi mot la cach nhanh
   * nhat de ho bo cuoc.
   */
  readonly reasons: RejectionReason[];

  constructor(status: number, payload: ApiErrorDetail) {
    super(payload.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.code;
    this.traceId = payload.trace_id;
    this.details = payload.details ?? {};
    this.reasons = Array.isArray(payload.details?.['reasons'])
      ? (payload.details['reasons'] as RejectionReason[])
      : [];
  }

  /** Danh sach thong diep de hien thi: nhieu ly do neu co, khong thi mot. */
  get messages(): string[] {
    return this.reasons.length > 0 ? this.reasons.map((r) => r.message) : [this.message];
  }
}

// ---------------------------------------------------------------------------
// Ma truy cap
// ---------------------------------------------------------------------------

let memoryToken: string | null = null;

export function setToken(token: string | null): void {
  memoryToken = token;
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Trinh duyet chan luu tru: van chay duoc trong phien hien tai.
  }
}

export function getToken(): string | null {
  if (memoryToken) return memoryToken;
  try {
    memoryToken = sessionStorage.getItem(TOKEN_KEY);
  } catch {
    memoryToken = null;
  }
  return memoryToken;
}

/** Goi khi phien het han: xoa ma va bao cho ung dung dua nguoi dung ve dang nhap. */
let onUnauthorised: (() => void) | null = null;
export function setUnauthorisedHandler(handler: () => void): void {
  onUnauthorised = handler;
}

// ---------------------------------------------------------------------------
// Khoa bat bien
// ---------------------------------------------------------------------------

/**
 * ID-01: moi thao tac ghi tien phai co khoa bat bien.
 *
 * Khoa sinh MOT LAN cho mot y dinh cua nguoi dung, khong sinh lai khi thu lai —
 * do la toan bo muc dich: bam hai lan hoac mang chap chon khong duoc tao hai lenh.
 */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `idm-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Goi
// ---------------------------------------------------------------------------

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  /** Bat buoc voi thao tac ghi tien. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Bo qua ma truy cap (dung cho dang ky, dang nhap). */
  anonymous?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  if (!options.anonymous) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal) init.signal = options.signal;

  const response = await fetch(path, init);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const envelope = (payload as { error?: ApiErrorDetail } | null)?.error;
    if (envelope) {
      if (response.status === 401 && onUnauthorised) onUnauthorised();
      throw new ApiError(response.status, envelope);
    }
    throw new ApiError(response.status, {
      code: 'SYSTEM_INTERNAL',
      message: `Loi khong xac dinh (${response.status})`,
      trace_id: '',
      details: {},
    });
  }

  return payload as T;
}

// ---------------------------------------------------------------------------
// Kieu du lieu tra ve
// ---------------------------------------------------------------------------

export interface Session {
  access_token: string;
  session_id: string;
  expires_at: string;
  player_id: string;
  mfa_required: boolean;
  new_device: boolean;
}

export interface Me {
  player_id: string;
  username: string;
  status: string;
  kyc_tier: string;
  currency: string;
  country_code: string;
  restrictions: string[];
  reality_check_due: boolean;
}

export interface WalletRow {
  wallet_id: string;
  currency: string;
  available: MoneyDto;
  bonus: MoneyDto;
  locked: MoneyDto;
  display_total: MoneyDto;
  frozen: boolean;
}

export interface GameSummary {
  code: string;
  name: string;
  category: string;
  provider_code: string;
  rtp_bp: number | null;
  volatility: string | null;
  has_demo: boolean;
  has_jackpot: boolean;
  thumbnail_url: string | null;
  status: 'ACTIVE' | 'MAINTENANCE' | 'DISABLED';
  min_bet: MoneyDto | null;
  max_bet: MoneyDto | null;
}

export interface SportEvent {
  id: string;
  sport_code: string;
  home_name: string;
  away_name: string | null;
  starts_at: string;
  status: string;
}

export interface Selection {
  id: string;
  name: string;
  odds_bp: number;
  odds: string;
  status: string;
}

export interface Market {
  id: string;
  market_type: string;
  parameter: string | null;
  period: string;
  status: string;
  selections: Selection[];
}

export interface DepositMethod {
  code: string;
  display_name: string;
  kind: 'EWALLET' | 'BANK_GATEWAY' | 'BANK_MANUAL' | 'CRYPTO' | 'VOUCHER';
  min_amount: MoneyDto;
  max_amount: MoneyDto;
  expiry_minutes: number;
}

export interface DepositCreated {
  deposit_id: string;
  status: string;
  /** Noi dung chuyen khoan — nguoi choi PHAI ghi dung thi tien moi khop duoc. */
  reference_code: string;
  expires_at: string | null;
  amount: MoneyDto;
}

export interface DepositRow {
  deposit_id: string;
  status: string;
  reference_code: string;
  amount: MoneyDto;
  received: MoneyDto | null;
}

export interface PayoutAccount {
  id: string;
  kind: string;
  account_name: string;
  bank_code: string | null;
  status: 'PENDING' | 'ACTIVE' | 'BLOCKED' | 'REMOVED';
  /** WDR-R31: tai khoan moi them bi chan 24 gio. */
  usable_from: string;
}

export interface Eligibility {
  eligible: boolean;
  blockers: RejectionReason[];
  max_withdrawable: MoneyDto;
  /** WDR-R11: chua dat doanh so cuoc toi thieu thi rut som bi tru phi. */
  requires_turnover_fee: boolean;
}

export interface WithdrawalCreated {
  withdrawal_id: string;
  status: string;
  amount: MoneyDto;
  fee: MoneyDto;
  net_amount: MoneyDto;
}

export interface LimitRow {
  period: string;
  cap_minor: string | null;
  used_minor: string;
  remaining_minor: string | null;
  scope: string;
}

export interface LimitsResponse {
  currency: string;
  limits: Record<string, LimitRow[]>;
}

export type LimitDimension = 'DEPOSIT' | 'STAKE' | 'NET_LOSS' | 'WITHDRAWAL' | 'SESSION';
export type LimitPeriod = 'DAY' | 'WEEK' | 'MONTH' | 'SINGLE';

export interface PlayerSession {
  id: string;
  device_fp: string | null;
  ip: string | null;
  created_at: string;
  last_seen_at: string;
}

export interface KycTierInfo {
  name: string;
  rank: number;
  withdrawal_allowed: boolean;
  deposit_cap_minor: string | null;
  withdrawal_cap_minor: string | null;
}

/**
 * Yeu cau dinh danh, do goi phap ly quy dinh (NT-07).
 * Ten tang KHONG duoc viet cung trong giao dien: moi noi cap phep dat ten khac.
 */
export interface KycRequirements {
  tiers: KycTierInfo[];
  tier_required_for_withdrawal: string;
  documents_required: string[];
  review_sla_hours: number;
}

/** Cac muc chon do goi phap ly quy dinh (NT-07) — khong viet cung trong giao dien. */
export interface ResponsibleGamingOptions {
  cooling_off_days: number[];
  self_exclusion_days: number[];
  session_reminder_minutes: number;
  limit_increase_cooldown_hours: number;
}

export type BetType = 'SINGLE' | 'MULTI' | 'SYSTEM';
export type OddsChangePolicy = 'REJECT' | 'ACCEPT_HIGHER' | 'ACCEPT_ANY';

export interface BetslipRequest {
  bet_type: BetType;
  legs: { selection_id: string; expected_odds_bp?: number }[];
  stake_minor: string;
  system_size?: number;
  odds_change_policy?: OddsChangePolicy;
}

export interface ValidateResult {
  acceptable: boolean;
  blockers: RejectionReason[];
  total_odds_bp: string | null;
  total_odds: string | null;
  combination_count: number;
  accepted_stake: MoneyDto;
  requested_stake: MoneyDto;
  potential_win: MoneyDto;
  /** SPT-R13: tran chi tra moi phieu — phai hien truoc khi xac nhan. */
  max_payout: MoneyDto;
  /** SPT-R21: do tre chap nhan cuoc truc tiep. */
  live_delay_ms: number;
  legs: { selection_id: string; server_odds_bp: number; odds_moved: 'NONE' | 'BETTER' | 'WORSE' }[];
}

export interface PlaceResult {
  bet_id: string;
  status: string;
  accepted_stake: MoneyDto;
  requested_stake: MoneyDto;
  total_odds: string | null;
  potential_win: MoneyDto;
  max_payout: MoneyDto;
  /** SPT-R25: may chu chi nhan mot phan khi cham gioi han. */
  partially_accepted: boolean;
}

export interface BetRow {
  bet_id: string;
  bet_type: string;
  status: string;
  stake: MoneyDto;
  total_odds: string | null;
  potential_win: MoneyDto;
  payout: MoneyDto | null;
  placed_at: string;
  settled_at: string | null;
}

/** SPT-R24: anh chup luc dat cuoc — man hinh lich su doc tu day, khong doc du lieu hien tai. */
export interface BetLegSnapshot {
  selection_name?: string;
  market_type?: string;
  market_parameter?: string | null;
  odds_bp?: number;
  event_id?: string;
  event_name?: string;
  sport_code?: string;
  starts_at?: string;
  captured_at?: string;
}

export interface BetLeg {
  selection_id: string;
  odds: string;
  result: string | null;
  snapshot: BetLegSnapshot;
}

export interface BetDetail {
  bet_id: string;
  status: string;
  bet_type: string;
  stake: MoneyDto;
  payout: MoneyDto | null;
  legs: BetLeg[];
}

/**
 * Gia chao rut cuoc som.
 *
 * SPT-R41: gia chao co thoi han RAT NGAN va phai duoc may chu xac nhan lai truoc
 * khi thuc hien. Giao dien khong duoc tu chot theo con so dang hien.
 */
export type CashoutUnavailableReason =
  | 'BET_NOT_OPEN'
  | 'SYSTEM_BET_UNSUPPORTED'
  | 'LEG_ALREADY_LOST'
  | 'MARKET_SUSPENDED'
  | 'LIVE_FEED_STALE'
  | 'READY_TO_SETTLE'
  | 'AMOUNT_TOO_SMALL';

export interface CashoutQuote {
  available: boolean;
  reasonCode?: CashoutUnavailableReason;
  reason?: string;
  quoteId?: string;
  amountMinor?: string;
  currency?: string;
  fractionBp?: number;
  expiresAt?: string;
}

export interface CashoutResult {
  betId: string;
  status: 'CASHED_OUT' | 'OPEN';
  paidMinor: string;
  remainingStakeMinor: string;
}

export interface BonusRow {
  bonus_id: string;
  campaign_code: string;
  amount: MoneyDto;
  wagering_required: MoneyDto;
  wagering_progress: MoneyDto;
  wagering_remaining: MoneyDto;
  max_bet_while_active: MoneyDto | null;
  expires_at: string;
}

/** Cach mot san pham xo so quy dinh bo so duoc chon. */
export interface LotteryNumberConfig {
  pick: number;
  min: number;
  max: number;
  allow_repeats: boolean;
}

export interface LotteryPrizeTier {
  matched: number;
  /** Boi so tra thuong, don vi diem co ban: 10000 = mot lan tien cuoc. */
  multiplier_bp: number;
  tier: string;
}

export interface LotteryPrizeTable {
  play_types: Record<string, LotteryPrizeTier[]>;
}

export type LotteryPlayType =
  | 'EXACT'
  | 'ANY_ORDER'
  | 'PICK_K_OF_N'
  | 'KENO'
  | 'GROUP'
  | 'LO'
  | 'DE';

/** Ma cua cuoc nhom. Y nghia co dinh o may chu, man hinh chi hien thi. */
export type LotteryOutcomeCode = 'CHAN' | 'LE' | 'TAI' | 'XIU';

export interface LotteryGroupOutcome {
  code: LotteryOutcomeCode;
  name: string;
  multiplier_bp: number;
}

export interface LotteryGroupMarket {
  code: string;
  name: string;
  prize_index: number;
  /** Lay may chu so cuoi cua giai do: 1 cho 0..9, 2 cho 0..99. */
  digits: number;
  outcomes: LotteryGroupOutcome[];
}

export interface LotteryResultPosition {
  label: string;
  digits: number;
}

/** Cau hinh bang ket qua nhieu giai. Null voi san pham kieu cu. */
export interface LotteryBoardConfig {
  positions: LotteryResultPosition[];
  tail_digits: number;
  de_prize_index: number;
  group_markets: LotteryGroupMarket[];
}

export interface LotteryProduct {
  code: string;
  name: string;
  kind: string;
  number_config: LotteryNumberConfig;
  prize_table: LotteryPrizeTable;
  board_config: LotteryBoardConfig | null;
  /** Kieu choi san pham thuc su nhan cuoc — may chu quyet dinh, khong tu suy. */
  play_types: LotteryPlayType[];
  rtp_bp: number;
  min_line_stake: MoneyDto;
  max_line_stake: MoneyDto;
  /** LOT-R33: tran chi tra moi ve, phai hien truoc khi nguoi choi xac nhan. */
  max_payout_per_ticket: MoneyDto;
}

export interface LotteryDraw {
  draw_id: string;
  product_code: string;
  draw_number: string;
  closes_at: string;
  draws_at: string;
  status: string;
  /** LOT-R51: bam hat giong cong bo TRUOC khi mo ban — co no moi kiem chung duoc. */
  seed_hash: string | null;
}

export interface LotteryLine {
  play_type: LotteryPlayType;
  numbers: number[];
  stake_minor: string;
  /** Chi cua cuoc nhom moi co: "<ma cua>:<ma lua chon>". */
  selection?: string;
}

export interface LotteryQuote {
  lines: number;
  total_stake: MoneyDto;
  /** Tien nhan lon nhat THAT SU cua ve nay, da ap tran chi tra. */
  max_payout: MoneyDto;
  max_payout_per_ticket: MoneyDto;
  /** LOT-R33: tran da cat bot muc tra hay chua. */
  capped: boolean;
  uncapped_payout: MoneyDto;
}

export interface LotteryTicketLineDetail {
  play_type: LotteryPlayType;
  numbers: number[];
  selection: string | null;
  /** Nhan doc duoc cua cua cuoc, may chu giai tu cau hinh san pham. */
  selection_label: string | null;
  matched: number | null;
  prize_tier: string | null;
  payout: MoneyDto | null;
  /** Vi tri tren bang ket qua ma so cua dong nay khop. */
  hit_positions: number[] | null;
}

export interface LotteryResultCell {
  label: string;
  digits: number;
  value: number | null;
}

export interface LotteryTicketDetail {
  ticket_id: string;
  status: string;
  product_code: string;
  product_name: string;
  draw_number: string;
  draw_status: string;
  draws_at: string;
  stake: MoneyDto;
  payout: MoneyDto | null;
  max_payout: MoneyDto;
  purchased_at: string;
  result_board: LotteryResultCell[] | null;
  result_numbers: number[] | null;
  lines: LotteryTicketLineDetail[];
}

export interface LotteryTicketCreated {
  ticket_id: string;
  lines: unknown[];
  total_stake: MoneyDto;
  max_payout: MoneyDto;
}

export interface LotteryTicketRow {
  ticket_id: string;
  draw_id: string;
  status: string;
  stake: MoneyDto;
  payout: MoneyDto | null;
  purchased_at: string;
}

export interface LotteryVerification {
  seed_hash: string | null;
  seed_revealed: string | null;
  result: { numbers: number[] } | null;
  algorithm: string;
  verifiable: boolean;
}

export interface PromotionRow {
  code: string;
  name: string;
  type: string;
  currency: string;
  starts_at: string;
  ends_at: string | null;
  opted_in: boolean;
}

export interface LoyaltyInfo {
  tier_code: string;
  tier_name: string | null;
  points: string;
  benefits: Record<string, unknown>;
  tier_since: string;
  next_tier: { code: string; name: string; points_needed: string } | null;
}

// ---------------------------------------------------------------------------
// Cac loi goi
// ---------------------------------------------------------------------------

export const api = {
  auth: {
    register: (body: Record<string, unknown>) =>
      request<{ player_id: string; status: string; verification_channel: string; verification_code?: string }>(
        '/v1/auth/register',
        { method: 'POST', body, anonymous: true },
      ),
    verify: (playerId: string, code: string) =>
      request<{ status: string }>('/v1/auth/verify-contact', {
        method: 'POST',
        body: { player_id: playerId, code },
        anonymous: true,
      }),
    login: (identifier: string, password: string, deviceFingerprint?: string) =>
      request<Session>('/v1/auth/login', {
        method: 'POST',
        body: { identifier, password, device_fingerprint: deviceFingerprint },
        anonymous: true,
      }),
    logout: () => request<{ status: string }>('/v1/auth/logout', { method: 'POST' }),
  },

  me: () => request<Me>('/v1/me'),

  wallet: {
    list: () => request<{ wallets: WalletRow[] }>('/v1/wallet'),
    transactions: (limit = 50) =>
      request<{ transactions: unknown[]; next_cursor: string | null }>(
        `/v1/wallet/transactions?limit=${limit}`,
      ),
  },

  casino: {
    lobby: (params: { category?: string; search?: string; sort?: string; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.category) q.set('category', params.category);
      if (params.search) q.set('search', params.search);
      if (params.sort) q.set('sort', params.sort);
      q.set('limit', String(params.limit ?? 60));
      return request<{ total: number; games: GameSummary[] }>(`/v1/casino/lobby?${q}`);
    },
    openSession: (gameCode: string, mode: 'REAL' | 'DEMO') =>
      request<{ session_id: string; launch_url: string; expires_at: string; mode: string }>(
        '/v1/casino/sessions',
        { method: 'POST', body: { game_code: gameCode, mode } },
      ),
    jackpots: () =>
      request<{ jackpots: { code: string; display_name: string; amount: MoneyDto; updated_at: string }[] }>(
        '/v1/casino/jackpots',
      ),
  },

  sports: {
    list: () => request<{ sports: { code: string; name: string; events: number }[] }>('/v1/sports'),
    events: (sportCode?: string, live?: boolean) => {
      const q = new URLSearchParams();
      if (sportCode) q.set('sport_code', sportCode);
      if (live !== undefined) q.set('live', String(live));
      return request<{ events: SportEvent[] }>(`/v1/sports/events?${q}`);
    },
    markets: (eventId: string) => request<{ markets: Market[] }>(`/v1/sports/events/${eventId}`),
  },

  deposit: {
    methods: () => request<{ methods: DepositMethod[] }>('/v1/deposit/methods'),
    create: (body: { method_code: string; amount_minor: string; currency: string }, key: string) =>
      request<DepositCreated>('/v1/deposit', { method: 'POST', body, idempotencyKey: key }),
    get: (id: string) => request<{ deposit: DepositRow | null }>(`/v1/deposit/${id}`),
  },

  withdrawal: {
    /**
     * Diem cuoi BAT BUOC goi TRUOC khi cho nguoi choi bam rut (docs/22 muc 9).
     * Tra ve TOAN BO vuong mac chu khong dung o vuong mac dau tien.
     */
    eligibility: (amountMinor?: string, payoutAccountId?: string) => {
      const q = new URLSearchParams();
      if (amountMinor) q.set('amount_minor', amountMinor);
      if (payoutAccountId) q.set('payout_account_id', payoutAccountId);
      return request<Eligibility>(`/v1/withdrawal/eligibility?${q}`);
    },
    create: (
      body: { payout_account_id: string; amount_minor: string; accept_turnover_fee?: boolean },
      key: string,
    ) => request<WithdrawalCreated>('/v1/withdrawal', { method: 'POST', body, idempotencyKey: key }),
    cancel: (id: string) =>
      request<{ status: string }>(`/v1/withdrawal/${id}/cancel`, { method: 'POST' }),
  },

  payoutAccounts: {
    list: () => request<{ accounts: PayoutAccount[] }>('/v1/payout-accounts'),
    add: (body: {
      kind: 'BANK' | 'EWALLET' | 'CRYPTO';
      account_number: string;
      account_name: string;
      bank_code?: string;
      crypto_network?: string;
    }) => request<{ id: string; usable_from: string }>('/v1/payout-accounts', { method: 'POST', body }),
    remove: (id: string) =>
      request<{ status: string }>(`/v1/payout-accounts/${id}`, { method: 'DELETE' }),
  },

  bonuses: {
    /** WDR-R14: huy thuong de rut. Khong dao nguoc duoc. */
    forfeit: () =>
      request<{ forfeited: number }>('/v1/bonuses/forfeit', {
        method: 'POST',
        body: { confirm: true },
      }),
  },

  limits: () => request<LimitsResponse>('/v1/me/limits'),

  account: {
    sessions: () => request<{ sessions: PlayerSession[] }>('/v1/me/sessions'),
    revokeSession: (id: string) =>
      request<{ status: string }>(`/v1/me/sessions/${id}`, { method: 'DELETE' }),

    /**
     * RSK-R11: ha han muc co hieu luc NGAY, nang han muc phai qua thoi gian cho.
     * May chu tra ve moc hieu luc; giao dien phai hien lai cho nguoi choi.
     */
    setLimit: (body: {
      dimension: LimitDimension;
      period: LimitPeriod;
      amount_minor: string | null;
    }) => request<{ effective_from: string }>('/v1/me/limits', { method: 'PUT', body }),

    rgOptions: () => request<ResponsibleGamingOptions>('/v1/responsible-gaming/options'),

    kycRequirements: () => request<KycRequirements>('/v1/kyc/requirements'),

    coolingOff: (days: number) =>
      request<{ ends_at: string | null }>('/v1/me/cooling-off', {
        method: 'POST',
        body: { days },
      }),

    /** Tu loai tru: khong go duoc truoc han, ke ca khi nguoi choi doi y. */
    selfExclusion: (days: number) =>
      request<{ ends_at: string | null }>('/v1/me/self-exclusion', {
        method: 'POST',
        body: { days, confirm: true },
      }),

    submitKyc: (body: {
      target_tier: string;
      document_type: string;
      document_number: string;
      document_expiry?: string;
      full_name: string;
    }) => request<{ case_id: string; status: string }>('/v1/kyc/cases', { method: 'POST', body }),

    changePassword: (currentPassword: string, newPassword: string) =>
      request<{ status: string }>('/v1/me/password', {
        method: 'POST',
        body: { current_password: currentPassword, new_password: newPassword },
      }),
  },

  bets: {
    /**
     * Diem cuoi BAT BUOC goi truoc khi dat cuoc.
     * Tra ve muc nhan toi da va TRAN CHI TRA — ca hai phai hien cho nguoi choi
     * thay TRUOC khi ho bam dat cuoc (SPT-R13, docs/40 muc 9).
     */
    validate: (body: BetslipRequest) =>
      request<ValidateResult>('/v1/bets/validate', { method: 'POST', body }),

    place: (body: BetslipRequest & { accept_partial?: boolean }, idempotencyKey: string) =>
      request<PlaceResult>('/v1/bets', { method: 'POST', body, idempotencyKey }),

    list: (params: { status?: string; limit?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.status) q.set('status', params.status);
      q.set('limit', String(params.limit ?? 30));
      return request<{ bets: BetRow[]; next_cursor: string | null }>(`/v1/bets?${q}`);
    },

    get: (id: string) => request<{ bet: BetDetail | null }>(`/v1/bets/${id}`),

    /** SPT-R41: lay gia chao moi; gia cu het han thi may chu tu choi. */
    cashoutQuote: (id: string, fractionBp = 10_000) =>
      request<CashoutQuote>(`/v1/bets/${id}/cashout-quote?fraction_bp=${fractionBp}`),

    /** SPT-R42: khong dao nguoc duoc. */
    cashout: (id: string, quoteId: string) =>
      request<CashoutResult>(`/v1/bets/${id}/cashout`, {
        method: 'POST',
        body: { quote_id: quoteId },
      }),
  },

  promotions: {
    list: () => request<{ promotions: PromotionRow[] }>('/v1/promotions'),
    /** BNS-R05: nhan khuyen mai la hanh dong CHU DONG, khong tu gan. */
    optIn: (code: string) =>
      request<{ status: string }>(`/v1/promotions/${code}/opt-in`, { method: 'POST' }),
    bonuses: () => request<{ bonuses: BonusRow[] }>('/v1/bonuses'),
    loyalty: () => request<{ loyalty: LoyaltyInfo | null }>('/v1/loyalty'),
    claimStreak: (campaignCode: string) =>
      request<{ claimed: boolean; points?: string; reason?: string }>('/v1/loyalty/streak/claim', {
        method: 'POST',
        body: { campaign_code: campaignCode },
      }),
  },

  lottery: {
    products: () => request<{ products: LotteryProduct[] }>('/v1/lottery/products'),
    draws: (productCode?: string) => {
      const q = new URLSearchParams();
      if (productCode) q.set('product_code', productCode);
      return request<{ draws: LotteryDraw[] }>(`/v1/lottery/draws?${q}`);
    },
    /**
     * Diem cuoi bat buoc goi TRUOC khi mua ve: tra ve tran chi tra moi ve
     * (LOT-R33), thu nguoi choi phai nhin thay truoc khi xac nhan.
     */
    quote: (productCode: string, lines: LotteryLine[]) =>
      request<{ quote: LotteryQuote | null }>('/v1/lottery/tickets/quote', {
        method: 'POST',
        body: { product_code: productCode, lines },
      }),
    buy: (drawId: string, lines: LotteryLine[], key: string) =>
      request<LotteryTicketCreated>('/v1/lottery/tickets', {
        method: 'POST',
        body: { draw_id: drawId, lines },
        idempotencyKey: key,
      }),
    tickets: () => request<{ tickets: LotteryTicketRow[] }>('/v1/lottery/tickets'),
    /**
     * Chi tiet mot ve kem BANG KET QUA va vi tri da khop cua tung dong.
     * Day la thu tra loi duoc cau hoi "so cua toi co tren bang khong, o giai
     * nao" — mot trang thai "Khong trung" tron tru thi khong tra loi duoc.
     */
    ticket: (ticketId: string) =>
      request<{ ticket: LotteryTicketDetail | null }>(`/v1/lottery/tickets/${ticketId}`),
    /** LOT-R54: du lieu de nguoi choi TU kiem chung ky quay. */
    verify: (drawId: string) =>
      request<{ verification: LotteryVerification | null }>(`/v1/lottery/draws/${drawId}/verify`),
  },
};
